// src/utils/chatPhotos.js — v0.319.0 (2026-11-04)
// ---------------------------------------------------------------------------
// A photo for a chat message, from a file picked OR dragged in, on either side
// (the office's item card and the client's report link).
//
// Brett, 2026-11-04: "build it so i can drag photos into chat." Both chat
// boxes take a drop now, and a dropped file is less tidy than a picked one: a
// Windows drag often carries no type, an iPhone photo copied over is HEIC, a
// screenshot is a PNG. The servers accept JPEG, PNG or WebP only, so this
// turns whatever came in into one of those before it is held for sending:
//
//   HEIC                 converted to JPEG (heicPhotos.toBrowserImage)
//   JPEG / PNG / WebP    sent as it came (a huge frame brought to 4096 px)
//   anything else        a GIF, BMP or typeless file re-drawn as a JPEG
//
// A file that cannot be decoded at all throws, so the caller can name it and
// send nothing for it — never a broken picture in the chat.

import { toBrowserImage, mayBePhoto } from './heicPhotos';
import { makePhotoBlobs } from './imageVariants';

// Room for a picture inside the API's 16 MB data-URL cap, with margin.
const MAX_DATA_URL_CHARS = 15 * 1024 * 1024;
const SENDABLE = /^data:image\/(jpeg|png|webp);base64,/i;

export const MAX_CHAT_PHOTOS = 10;

export function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read that photo file'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = src;
  });
}

async function redrawAsJpeg(dataUrl) {
  const img = await loadImage(dataUrl);
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) throw new Error('image has no size');
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';               // a transparent PNG/GIF gets white, not black
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.92);
}

/** One file → a data URL the chat APIs accept. Throws when it is not a readable picture. */
export async function chatPhotoDataUrl(file) {
  const browserFile = await toBrowserImage(file);
  const { original, width } = await makePhotoBlobs(browserFile);
  let dataUrl = await readAsDataUrl(original || browserFile);
  // makePhotoBlobs returns no size when it could not decode the file at all.
  if (!width) await loadImage(dataUrl);            // throws: not a picture
  if (!SENDABLE.test(dataUrl) || dataUrl.length > MAX_DATA_URL_CHARS) dataUrl = await redrawAsJpeg(dataUrl);
  if (dataUrl.length > MAX_DATA_URL_CHARS) throw new Error('photo too large');
  return dataUrl;
}

// Page-wide keys, so two items' or two chats' photos never share one.
let picSeq = 0;

/**
 * Files picked or dropped → photos ready to wait in a chat box.
 *   room     how many more the box can take
 * → { pics: [{ key, dataUrl }], over: n left out for the limit, failed: [names] }
 * Non-picture files (a PDF, a Word file) are counted as failed by name.
 */
export async function photosForChat(files, room) {
  const all = Array.from(files || []).filter(Boolean);
  const failed = all.filter((f) => !mayBePhoto(f)).map((f) => f.name || 'A file');
  const list = all.filter((f) => mayBePhoto(f));
  const take = list.slice(0, Math.max(0, room));
  const pics = [];
  for (const f of take) {
    try {
      // eslint-disable-next-line no-await-in-loop
      pics.push({ key: ++picSeq, dataUrl: await chatPhotoDataUrl(f) });
    } catch (_e) {
      failed.push(f.name || 'A file');
    }
  }
  return { pics, over: list.length - take.length, failed };
}

/** The words for what photosForChat could not take. '' when it took everything. */
export function photosForChatError({ over, failed }, max = MAX_CHAT_PHOTOS) {
  const parts = [];
  if (failed.length) parts.push(`${failed.join(', ')} ${failed.length === 1 ? 'is not a photo that can be read' : 'are not photos that can be read'} — only photos can go in the chat.`);
  if (over > 0) parts.push(`Up to ${max} photos a message — ${over} left out.`);
  return parts.join(' ');
}
